import { Modal as Modals } from "@general-backoffice/core/index";
import React, { useCallback, useEffect, useMemo, useState } from "react";

interface UseModalOptions {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Modal?: React.ComponentType<any>;
  autoOpen?: boolean;
  onClose?: () => void;
  type?: string;
}

interface UseModalResult {
  Modal: React.FC<Record<string, unknown>>;
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
}

/**
 * Hook for managing modal state and rendering.
 * Provides open/close controls and a pre-configured Modal component.
 */
const useModal = ({ Modal: Component, autoOpen, onClose, type = "Simple" }: UseModalOptions): UseModalResult => {
  const [isOpen, setIsOpen] = useState(autoOpen || false);

  useEffect(() => {
    if (autoOpen) {
      setIsOpen(true);
    }
  }, [autoOpen]);

  const open = () => setIsOpen(true);
  const close = useCallback(() => {
    setIsOpen(false);
    if (onClose) onClose();
  }, [onClose]);
  const toggle = () => (!isOpen ? open() : close());

  // @general-backoffice/core exports Modal as a component with sub-components (Modal.Simple, etc.)
  // but doesn't provide proper index signature typing. We access sub-components via bracket notation.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const ModalsRecord: Record<string, React.ComponentType<any>> = Object.fromEntries(
    Object.entries(Modals).filter(([, v]) => typeof v === "function")
  );
  const ModalComponent = Component || ModalsRecord[type] || ModalsRecord.Simple;

  const Modal: React.FC<Record<string, unknown>> = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const ModalWrapper = (modalProps: any) => {
      const ModalEl = ModalComponent;
      return (
        <ModalEl
          {...modalProps}
          isOpen={isOpen}
          autoOpen={autoOpen}
          {...(Component ? { close } : { toggleOpen: close })}
        />
      );
    };
    return ModalWrapper;
  }, [isOpen, autoOpen, close, ModalComponent, Component]);

  return {
    Modal,
    isOpen,
    open,
    close,
    toggle,
  };
};

export default useModal;
